// Dependencies
import React, { useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {Box, Pagination, Stack, FormControlLabel, Switch, IconButton} from '@mui/material';

import styles from './styles';

// Components
import { CustomerDocDialog, IColumn, Table } from '../../components';

// Resources
import { ISortBy, ORDER } from '../../resource';

// Context
import { AppContext } from '../../context';

// Icons
import { CloseCircleIcon, SuccessIcon } from '../../assets/icons';
import {useDebounce} from '../../hooks/useDebounce';
import RecoverIcon from '@mui/icons-material/Refresh';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import apis from '../../apis';
import {useToastContext} from '../../components/Toast/toastContext';
import {useAlertContext} from '../../components/Alert/alertContext';
import moment from 'moment';

// Utils
import { useUrlParams } from '../../utils/urlParams';

// Constants
const pageLimit = 15;

// Create customer document page
export const CustomerDocumentPage = () => {
  // URL params hook
  const { updateUrlParams, updateUrlParamsAfterApiResponse, initializeFilters, getUrlParams, restoreFromStorage } = useUrlParams('customerDocuments');

  // States
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [sort, setSort] = useState<ISortBy>({
    field: 'name',
    order: ORDER.ASC,
  });
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const latestRequestId = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { activeToast } = useToastContext();
  const { activeAlert } = useAlertContext();

  // Context
  const {
    isAdmin,
    account,
    dialogs,
    customerDoc,
    customerDocs,
    search,
    setSearch,
    showDeleted,
    setShowDeleted,
    retentionConfig,
  } = useContext(AppContext);
  const debouncedSearch = useDebounce(search);

  const emptyStateText = useMemo(() => {
    const keyword = (search ?? '').trim();
    if (keyword && (customerDocs.value?.length ?? 0) === 0) {
      return `No document with search keyword: ${keyword}`;
    }
    return 'There is no data!';
  }, [search, customerDocs.value]);

  // Update the refs when they change
  const activeToastRef = useRef(activeToast);
  const customerDocsRef = useRef(customerDocs);

  useEffect(() => {
    activeToastRef.current = activeToast;
  }, [activeToast]);

  useEffect(() => {
    customerDocsRef.current = customerDocs;
  }, [customerDocs]);

  // Initialize filters from URL or localStorage
  useEffect(() => {
    console.log('Initializing CustomerDocument filters...');
    
    // Get current URL parameters
    const urlParams = getUrlParams();
    console.log('Current URL params:', urlParams);
    
    // Get stored filters
    const storedParams = restoreFromStorage();
    console.log('Stored params:', storedParams);
    
    // Determine which parameters to use
    let finalParams;
    
    if (Object.keys(urlParams).length > 0) {
      // URL has parameters - use them
      console.log('Using URL parameters');
      finalParams = urlParams;
    } else if (Object.keys(storedParams).length > 0) {
      // No URL params but stored params exist - use stored params
      console.log('Using stored parameters');
      finalParams = storedParams;
    } else {
      // No stored params - use defaults
      console.log('Using default parameters');
      finalParams = {
        page: 1,
        sort: 'name_asc',
        archived: false,
      };
    }
    
    console.log('Final params to apply:', finalParams);
    
    // Apply the final parameters
    setPageNumber(finalParams.page || 1);
    
    if (finalParams.sort) {
      const [field, order] = finalParams.sort.split('_');
      setSort({ field, order: order === 'asc' ? ORDER.ASC : ORDER.DESC });
    }
    
    if (finalParams.archived !== undefined) {
      setShowDeleted(finalParams.archived);
    }
    
    // Handle search parameter - only restore from URL params, not localStorage
    if (finalParams.search !== undefined && finalParams.search !== '') {
      setSearch(finalParams.search);
    }
    
    setIsInitialized(true);
  }, []);

  // Calc page count
  const pageCnt = useMemo(() => {
    return Math.ceil((customerDocs.pagination?.total || 0) / pageLimit);
  }, [customerDocs.pagination]);

  // Sort handler
  const handleSort = (value: ISortBy) => {
    setSort(value);
    updateUrlParams({ sort: `${value.field}_${value.order}` });
  };

  // Selected change handler
  const handleChangeSelected = (selected: (string | number)[]) => {
    customerDocs.setSelected(selected as string[]);
  };

  // Page change handler
  const handlePageChange = (_, page: number) => {
    setPageNumber(page);
    customerDocs.setSelected([]);
    updateUrlParams({ page });
  };

  // Close customer doc dialog
  const handleCloseCustomerDocDialog = () => {
    dialogs.customerDoc.setVisible(false);
  };

  // Update URL params when showDeleted changes
  useEffect(() => {
    if (isInitialized) {
      setPageNumber(1);
      updateUrlParams({ page: 1, archived: showDeleted });
    }
  }, [showDeleted, isInitialized]);

  // Fetch data when dependencies change
  useEffect(() => {
    if (!isInitialized || !account) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    latestRequestId.current += 1;
    const requestId = latestRequestId.current;

    const fetchData = async () => {
      try {
        await customerDocsRef.current.fetchData(account.id, {
          options: {
            limit: pageLimit,
            skip: (pageNumber - 1) * pageLimit,
            sort: {
              [sort.field]: sort.order,
            },
            search: debouncedSearch,
            archived: showDeleted ? 1 : 0,
          },
          signal: controller.signal,
          requestId,
        });
      } catch (err: any) {
        if (err.name === 'CanceledError' || err.message?.includes('canceled') || err?.canceled) {
          // Request was cancelled, do nothing
          return;
        }
        // Show real errors only
        activeToastRef.current({
          type: 'error',
          text: err.message || 'Failed to fetch customer documents',
        });
      }
    };

    fetchData();
  }, [isInitialized, pageNumber, sort, account?.id, showDeleted, debouncedSearch]);

  // On search changed - update URL after successful API response
  useEffect(() => {
    if (isInitialized && debouncedSearch !== undefined) {
      // Update URL params after a short delay to allow API response
      setTimeout(() => {
        updateUrlParamsAfterApiResponse({ search: debouncedSearch });
      }, 100);
    }
  }, [debouncedSearch, isInitialized]);

  const handleDeleteCustomerDocForever = async (id: string) => {
    activeAlert({
      type: 'info',
      text: "Are you sure to remove this customer document forever?",
    }, async () => {
      try {
        if (account) {
          const res = await apis.pdf.removeForeverCustomerDocument(account?.id, id);
          activeToastRef.current({
            type: 'success',
            text: res.msg,
          });
        } else {
          throw Error();
        }
      } catch (e) {
        activeToastRef.current({
          type: 'error',
          text: 'Failed to delete customer document. Please try again.',
        });
      }
    });
  };

  const handleRecoverCustomerDoc = async (id: string) => {
    activeAlert({
      type: 'info',
      text: "Are you sure to recover this customer document?",
    }, async () => {
      try {
        if (account) {
          const res = await apis.pdf.recoverCustomerDocument(account?.id, id);
          activeToastRef.current({
            type: 'success',
            text: res.msg,
          });
        }
      } catch (e) {
        activeToastRef.current({
          type: 'error',
          text: 'Failed to recover customer document. Please try again.',
        });
      }
    });
  };

  // Define columns
  const columns: IColumn[] = [
    {
      field: 'name',
      label: 'Name',
      width: 200,
    },
    {
      field: 'url',
      label: 'URL',
      render: ({ url }) => <Box sx={styles.longCell}><div>{url}</div></Box>
    },
    ...(isAdmin ? [{
      field: 'customer',
      label: 'Customer',
      width: 200,
      render: ({ customer }) => {
        return `${customer?.firstName || ''} ${customer?.lastName || ''}`
      }
    }] : []),
    {
      field: 'is_editable',
      label: 'Editable',
      sortable: false,
      width: 80,
      render: ({ is_editable }) =>
        is_editable ? <SuccessIcon height={20} width={20} /> : <CloseCircleIcon height={20} width={20} />,
    },
    {
      field: 'is_active',
      label: 'Active',
      sortable: false,
      width: 80,
      render: ({ is_active }) =>
        is_active ? <SuccessIcon height={20} width={20} /> : <CloseCircleIcon height={20} width={20} />,
    },
    ...(showDeleted ? [
      {
        field: 'delete_after',
        label: 'Will be deleted after',
        render: (row) => {
          const deletedAfter = retentionConfig.customerDocument - moment().diff(row.deletedAt, 'day');
          return `${deletedAfter > 1 ? `after ${deletedAfter} days` : deletedAfter === 1 ? 'tomorrow' : '' }`;
        }
      },
      {
        field: 'deletedActions',
        label: 'Actions',
        render: (row) => (
          <Box display="flex">
            {/*@ts-ignore*/}
            <IconButton variant="text" size="medium" onClick={() => handleRecoverCustomerDoc(row._id)}>
              <RecoverIcon />
            </IconButton>
            {/*@ts-ignore*/}
            <IconButton variant="text" size="medium" onClick={() => handleDeleteCustomerDocForever(row._id)}>
              <DeleteForeverIcon />
            </IconButton>
          </Box>
        )
      }
    ] : [])
  ];

  // Return customer documents page
  return (
    <>
      <Stack alignItems="center" spacing={{ xs: 36, md: 66 }}>
        <Table
          rowSelectable
          columns={columns}
          data={customerDocs.value}
          order={sort.order}
          orderBy={sort.field}
          isLoading={customerDocs.isLoading}
          emptyStateText={emptyStateText}
          selected={customerDocs.selected}
          contentSize={{
            width: {
              xs: 'calc(100vw - 40px)',
              sm: 'calc(100vw - 80px)',
              md: 'calc(100vw - 256px)',
              lg: 'calc(100vw - 376px)',
            },
          }}
          onSort={handleSort}
          onChangeSelected={handleChangeSelected}
        />
        {customerDocs.pagination && pageCnt > 1 && (
          <Pagination page={pageNumber} count={pageCnt} onChange={handlePageChange} />
        )}
      </Stack>
      {dialogs.customerDoc.visible && (
        <CustomerDocDialog
          open={dialogs.customerDoc.visible}
          document={customerDoc.value}
          onClose={handleCloseCustomerDocDialog}
        />
      )}
    </>
  );
};
